class RemoteForm extends HTMLElement {
    connectedCallback() {
        this.isCustomFeedback = this.dataset.customFeedback;
        this.disableScroll = this.dataset.disableScroll;
        this.submitMultiple = this.dataset.submitMultiple;
        this.apiUrl = this.dataset.apiUrl || ncol_api_url;

        this.form = this.querySelector("form");
        // this.id = this.form.id;
        this.formFeedback = this.form.querySelector('[name="_feedback"]');
        this.submitUrl = `${this.apiUrl}${this.form.dataset.action}`;
        this.form.addEventListener("submit", this.onSubmit.bind(this));
        this.setupTurnstile();
    }

    disconnectedCallback() {
        if (this.turnstile) turnstile.remove(this.turnstile);
    }

    setupTurnstile() {
        const remoteForm = this;
        const container = this.form.querySelector("[data-turnstile]");

        if (!container) return console.warn("RemoteForm: Turnstile not enabled because container not found.");

        if (container.dataset.invisible) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "cf-turnstile-key-invisible";
            input.value = "true";
            this.form.appendChild(input);
        }

        let firstRun = true;
        turnstile.ready(function () {
            if (!firstRun) return;
            firstRun = false;
            remoteForm.turnstile = turnstile.render(container, {
                theme: "light",
                sitekey: container.dataset.invisible ? cloudflare_turnstile_invisible_key : cloudflare_turnstile_key,
                action: `RemoteForm-${remoteForm.id}`,
                appearance: "interaction-only",
                callback: function (token) {
                    remoteForm.token = token;
                },
                'before-interactive-callback': function () {
                    container.removeAttribute('inert');
                }
            });
        });
    }

    setSubmitting(submitting) {
        this.submitting = submitting;
        if (!this.form.querySelector('[type="submit"]')) return;
        if (submitting) {
            this.form.querySelector('[type="submit"]').setAttribute("disabled", "disabled");
            this.form.querySelector('[type="submit"]').classList.add("loading");
        } else {
            this.form.querySelector('[type="submit"]').removeAttribute("disabled");
            this.form.querySelector('[type="submit"]').classList.remove("loading");
            turnstile.reset(this.turnstile);
        }
    }

    onSubmit(e) {
        e.preventDefault();
        if (this.submitting) return;
        this.setSubmitting(true);

        const formData = new FormData(this.form);

        const sanitizeRegex = this.form.querySelectorAll("[data-sanitize]");

        for (const [key, value] of formData.entries()) {
            if (sanitizeRegex.length > 0) {
                sanitizeRegex.forEach((item) => {
                    if (item.name !== key) return;
                    const regex = new RegExp(item.dataset.sanitize, "g");
                    formData.set(key, value.replace(regex, ""));
                });
            }
        }

        // Clear all errors.
        this.form.querySelectorAll("[data-error]").forEach((error) => error.remove());
        this.form.querySelectorAll(".field.error").forEach((field) => field.classList.remove("error"));
        this.formFeedback.removeAttribute("data-status");
        this.formFeedback.innerHTML = "";

        fetch(this.submitUrl, {
            method: this.form.dataset.method || "POST",
            body: formData
        })
            .then((response) => response.json())
            .then((json) => {
                if (json.status !== "success") throw json;

                if (this.submitMultiple) this.setSubmitting(false);
                else if (!this.isCustomFeedback) this.form.innerHTML = "";

                if (this.isCustomFeedback) {
                    this.formFeedback.textContent = json.message;
                    if (!this.disableScroll) this.formFeedback.scrollIntoView();
                    return;
                }

                const successContainer = document.createElement("div");
                successContainer.classList.add("flash", "flash--success");

                const successContent = document.createElement("div");
                successContent.classList.add("flash__content");

                if (json.title) {
                    const successTitle = document.createElement("h3");
                    successTitle.classList.add("flash__title");
                    successTitle.innerHTML = json.title;
                    successContent.appendChild(successTitle);
                }

                const successMessage = document.createElement("p");
                successMessage.classList.add("flash__message");
                successMessage.innerHTML = json.message || "Your form has been submitted.";
                successContent.appendChild(successMessage);

                successContainer.appendChild(successContent);
                this.form.appendChild(successContainer);
                if (!this.disableScroll) successContainer.scrollIntoView();
            })
            .catch((error) => {
                this.setSubmitting(false);
                this.formFeedback.dataset.status = "error";

                let firstError = false;
                let formError = "Something went wrong while trying to submit this form. Please try submitting again, or contact Customer Service for assistance.";

                // Unknown payload error.
                if (!error.status) {
                    console.error(error);
                    this.formFeedback.value = formError;
                    if (!this.disableScroll) this.formFeedback.scrollIntoView();
                    return;
                }

                // Insert field errors.
                const visibleErrors = [];
                const hiddenErrors = [];
                if (error.fields && error.fields.length > 0) {
                    error.fields.forEach((field) => {
                        const input = this.form.querySelector(`[name="${field.name}"]`);

                        // Skip if field is missing from form or hidden.
                        if (!input || input.type === "hidden") {
                            hiddenErrors.push(field.name);
                            return;
                        }

                        // Skip duplicate error message.
                        if (visibleErrors.includes(field.name)) return;

                        visibleErrors.push(field.name);

                        // Create error message and add to DOM.
                        const container = input.parentElement;
                        container.classList.add("error");

                        const errorMessage = document.createElement("small");
                        errorMessage.classList.add("form__message", "form__message--error");
                        errorMessage.dataset.error = "true";
                        errorMessage.innerHTML = field.message;
                        container.appendChild(errorMessage);

                        if (!firstError) firstError = input.id;
                    });
                }

                // Insert form error message.
                if (error.fields && visibleErrors.length > 0 && hiddenErrors.length < visibleErrors.length) {
                    const firstErrorLink = document.createElement("a");
                    firstErrorLink.href = `#${firstError}`;
                    firstErrorLink.textContent = `Go to ${visibleErrors.length > 1 ? "first" : "the"} error.`;

                    this.formFeedback.textContent = `Please make changes to ${visibleErrors.length} field${visibleErrors.length !== 1 ? "s" : ""} and submit this form again. `;
                    this.formFeedback.appendChild(firstErrorLink);
                } else if (hiddenErrors.length > 0) formError = `Something went wrong with the form submission. Please refresh this page and try submitting this form again.`;
                else if (error.message) formError = error.message;

                if (!this.formFeedback.textContent) this.formFeedback.textContent = formError;
                if (!this.disableScroll) this.formFeedback.scrollIntoView();

                if (error.action) {
                    if (error.action === "reload") location.reload();
                }
            });
    }
}

customElements.define("remote-form", RemoteForm);
